import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { getUser } from '../lib/auth';
import {
  getTheme,
  setTheme,
  notificationsEnabled,
  setNotificationsEnabled,
  requestNotifications,
} from '../lib/prefs';
import { formatListTime } from '../lib/format';

// Settings panel — chat + groups context only (no posts/stories). Profile from
// getUserInfo, notifications + theme prefs, linked devices, and logout.
export default function SettingsDrawer({ onClose, onLogout }) {
  const stored = getUser();
  const [profile, setProfile] = useState(null);
  const [devices, setDevices] = useState([]);
  const [theme, setThemeState] = useState(getTheme());
  const [notif, setNotif] = useState(notificationsEnabled());

  useEffect(() => {
    if (stored?.userId) {
      api
        .get(`/api/qr/user/${stored.userId}`)
        .then(({ data }) => setProfile(data.user))
        .catch((err) => console.error('Profile load failed:', err));
    }
    api
      .get('/api/qr/linked-devices')
      .then(({ data }) => setDevices(data.devices || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const name = profile?.name || stored?.userName || 'NearIt user';
  const photo = profile?.photoURL;

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setThemeState(next);
  };

  const toggleNotif = async () => {
    if (!notif) {
      const ok = await requestNotifications();
      setNotif(ok);
    } else {
      setNotificationsEnabled(false);
      setNotif(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-[380px] h-full bg-white shadow-xl flex flex-col">
        <header className="h-16 px-4 flex items-center gap-3 border-b border-slate-200 shrink-0">
          <button onClick={onClose} className="text-muted text-xl" title="Close">
            ←
          </button>
          <span className="font-medium text-ink">Settings</span>
        </header>

        <div className="flex-1 overflow-y-auto">
          {/* Profile */}
          <div className="p-4 flex items-center gap-3 border-b border-slate-200">
            {photo ? (
              <img src={photo} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-brand text-white grid place-items-center text-xl font-semibold">
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-medium text-ink truncate">{name}</div>
              {profile?.phoneNumber && (
                <div className="text-sm text-muted">{profile.phoneNumber}</div>
              )}
              {profile?.bio && <div className="text-xs text-muted truncate">{profile.bio}</div>}
            </div>
          </div>

          {/* Preferences */}
          <div className="p-2">
            <Row
              label="Desktop notifications"
              hint="Alert me when a message arrives and the tab is in the background"
            >
              <Toggle on={notif} onClick={toggleNotif} />
            </Row>
            <Row label="Dark theme">
              <Toggle on={theme === 'dark'} onClick={toggleTheme} />
            </Row>
          </div>

          {/* Linked devices */}
          <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">
            Linked devices
          </div>
          <div className="px-2 pb-3">
            {devices.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted">This device only.</div>
            ) : (
              devices.map((d) => (
                <div key={d.qrToken} className="flex items-center gap-3 px-2 py-2">
                  <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center">💻</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink truncate">
                      {d.deviceType === 'mobile' ? 'Mobile' : 'Web'} · {d.userName || 'Device'}
                    </div>
                    <div className="text-xs text-muted">
                      {d.connectedAt ? `Linked ${formatListTime(d.connectedAt)}` : 'Linked'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 shrink-0">
          <button
            onClick={onLogout}
            className="w-full py-2 rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-3 px-2 py-3">
      <div className="min-w-0">
        <div className="text-sm text-ink">{label}</div>
        {hint && <div className="text-xs text-muted">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${
        on ? 'bg-brand' : 'bg-slate-300'
      }`}
      aria-pressed={on}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
          on ? 'left-[22px]' : 'left-0.5'
        }`}
      />
    </button>
  );
}
